import React, { useState, useImperativeHandle, forwardRef, useRef } from 'react';
import { flushSync } from 'react-dom';
import '../styles/consent.scss';

// 마케팅 정보 수신(선택) 기본 본문.
// 랜스(스튜디오)가 marketingPolicy를 내려주면 그 값이 우선한다.
const DEFAULT_MARKETING_POLICY = `
  <strong>[마케팅 활용 및 수신 동의]</strong><br/>
  *이용자는 개인정보에 대해 마케팅 활용 동의 및 수신 거부할 권리가 있으나 동의를 거부할 경우 상품 및 이벤트 정보, 이벤트 참여, 상담 등에 제약이 발생할 수 있습니다.
`;

const ZentorgaForm = forwardRef(({
  onSubmit,
  formStyle = 'gray',
  consentStyle = 'dark',
  privacyPolicyName = '본원',
  privacyPolicy = null,
  marketingPolicy = null,
  children,
  buttonComponent,
  isSubmitting: externalIsSubmitting = false  // 외부 isSubmitting 상태 받기
}, ref) => {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState({ first: '010', second: '', third: '' });
  const [isAgreed, setIsAgreed] = useState(false);
  const [isMarketingAgreed, setIsMarketingAgreed] = useState(false);
  // null | 'privacy' | 'marketing' — 한 번에 하나만 펼친다
  const [openDetail, setOpenDetail] = useState(null);

  const marketingPolicyHtml = String(marketingPolicy || '').trim()
    ? String(marketingPolicy).replace(/\n/g, '<br/>')
    : DEFAULT_MARKETING_POLICY;

  // 외부에서 전달된 isSubmitting이 있으면 사용, 없으면 내부 상태 사용
  const [internalIsSubmitting, setInternalIsSubmitting] = useState(false);
  const isSubmitting = externalIsSubmitting || internalIsSubmitting;

  const handlePhoneNumberChange = (e) => {
    const { name, value } = e.target;
    setPhoneNumber(prev => ({ ...prev, [name]: value.replace(/[^0-9]/g, '') }));
  };

  const nameRef = useRef(null);
  const validateName = (name) => {
    if (!name.trim()) {
    nameRef.current?.focus();
    return false;
  }
    // 한글, 영어, 공백만 허용
    const validNameRegex = /^[가-힣a-zA-Z\s]+$/;
    if (!validNameRegex.test(name)) {
      alert('이름은 한글, 영어만 입력 가능합니다.');
      return false;
    }

    if (name.length < 2) {
      alert('이름은 2자 이상이어야 합니다.');
      return false;
    }

    // 한글만 입력된 경우 5자 제한
    const koreanOnlyRegex = /^[가-힣]+$/;
    if (koreanOnlyRegex.test(name) && name.length > 5) {
      alert('한글 이름은 5자 이하여야 합니다.');
      return false;
    }

    // 영어가 포함된 경우 20자 제한
    if (name.length > 20) {
      alert('이름은 20자 이하여야 합니다.');
      return false;
    }

    return true;
  };

  const validatePhoneNumber = (phoneNumber) => {
    const { first, second, third } = phoneNumber;
    const fullNumber = first + second + third;

    if (fullNumber.length !== 11) {
      alert('전화번호 11자리를 모두 입력해주세요.');
      return false;
    }

    // 반복되는 숫자 체크
    if (/(\d)\1{10}/.test(fullNumber)) {
      alert('유효하지 않은 전화번호입니다.');
      return false;
    }

    return true;
  };

// handleSubmit 안의 로직을 submitForm으로 분리
const submitForm = async () => {
  if (!validateName(name)) return;
  if (!validatePhoneNumber(phoneNumber)) return;
  if (!isAgreed) {
    const confirmed = window.confirm(' 확인을 누르시면 개인정보처리방침에 동의하여 상담 신청이 완료됩니다.');
    if (!confirmed) return;
    setIsAgreed(true);
  }
  if (!externalIsSubmitting) flushSync(() => setInternalIsSubmitting(true));
  try {
    await onSubmit({ name, phoneNumber });
  } catch (error) {
    console.error('Form submission error:', error);
    alert('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
  } finally {
    if (!externalIsSubmitting) setInternalIsSubmitting(false);
  }
};

// 부모에서 호출할 수 있도록 노출
useImperativeHandle(ref, () => ({
  submitForm: () => {
    setTimeout(() => submitForm(), 500);
  }
}));

// handleSubmit은 submitForm 호출만
const handleSubmit = async (e) => {
  e.preventDefault();
  await submitForm();
};

  return (
    <>
      {isSubmitting && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99998,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.55)',
        }}>
          <style>{`@keyframes zentorgaSpin { to { transform: rotate(360deg) } }`}</style>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px', height: '48px',
              border: '4px solid rgba(255,255,255,0.3)',
              borderTopColor: '#fff',
              borderRadius: '50%',
              animation: 'zentorgaSpin 0.8s linear infinite',
            }} />
            <p style={{ color: '#fff', fontSize: '16px', fontWeight: 600, margin: 0 }}>신청 중입니다</p>
          </div>
        </div>
      )}
    <form
      id="zentorga-form"
      className={`zentorga-form formStyleClassName form-${formStyle}`}
      onSubmit={handleSubmit}
    >
      <div className="input-wrap">
        <div className="input-user">
          <label htmlFor="name">이름</label>
          <input
            ref={nameRef}
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="입력*"
            required
          />
        </div>
        <div className="input-contact">
          <label htmlFor="phoneFirst">연락처</label>
          <div className="phone-wrap">
            <input
              type="tel"
              name="first"
              value={phoneNumber.first}
              maxLength="3"
              placeholder="010"
              onChange={handlePhoneNumberChange}
              required
            />
            <input
              type="tel"
              name="second"
              value={phoneNumber.second}
              maxLength="4"
              placeholder="0000"
              onChange={handlePhoneNumberChange}
              required
            />
            <input
              type="tel"
              name="third"
              value={phoneNumber.third}
              maxLength="4"
              placeholder="0000"
              onChange={handlePhoneNumberChange}
              required
            />
          </div>
        </div>
      </div>

      {children}

      <div className={`consentClassName consent-${consentStyle} zt-consent-v2`} id="consentClassName">
        <div className="zt-consent-row zt-consent-row-all">
          <label className="zt-consent-label">
            <input
              type="checkbox"
              id="consent-all"
              checked={isAgreed && isMarketingAgreed}
              onChange={(e) => {
                setIsAgreed(e.target.checked);
                setIsMarketingAgreed(e.target.checked);
              }}
            />
            <span className="zt-consent-title">전체 동의</span>
          </label>
        </div>

        <div className="zt-consent-row">
          <label className="zt-consent-label">
            <input
              type="checkbox"
              id="zentorga-consent"
              className="zentorga-consent"
              checked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
            />
            <span className="zt-consent-title">상담/예약 서비스 제공 동의 (필수)</span>
          </label>
          <button
            type="button"
            className="toggle-detail"
            onClick={() => setOpenDetail(openDetail === 'privacy' ? null : 'privacy')}
          >
            [{openDetail === 'privacy' ? '닫기' : '자세히보기'}]
          </button>
        </div>

        <div className="zt-consent-row">
          <label className="zt-consent-label">
            <input
              type="checkbox"
              id="consent-marketing"
              checked={isMarketingAgreed}
              onChange={(e) => setIsMarketingAgreed(e.target.checked)}
            />
            <span className="zt-consent-title">마케팅 정보 수신 (선택)</span>
          </label>
          <button
            type="button"
            className="toggle-detail"
            onClick={() => setOpenDetail(openDetail === 'marketing' ? null : 'marketing')}
          >
            [{openDetail === 'marketing' ? '닫기' : '자세히보기'}]
          </button>
        </div>
      </div>

      {openDetail === 'privacy' && privacyPolicy && (
        <div className="privacyPolicy show">
          <div dangerouslySetInnerHTML={{ __html: String(privacyPolicy).replace(/\n/g, '<br/>') }} />
        </div>
      )}

      {openDetail === 'marketing' && (
        <div className="privacyPolicy zt-marketing-policy show">
          <div dangerouslySetInnerHTML={{ __html: marketingPolicyHtml }} />
        </div>
      )}

      {buttonComponent || (
        <button
          type="submit"
          className="postBtn"
          disabled={isSubmitting}
        >
          {isSubmitting ? '신청 중...' : '신청하기'}
        </button>
      )}
    </form>
    </>
  );
});

export default ZentorgaForm;
